import { ApiClient } from './client';
import { usersAPI } from './users.api';

export interface WeeklyGoal {
  userId: string;
  targetStories: number;
  currentProgress: number;
  progressPercentage: number;
  weekStartDate: string;
  weekEndDate: string;
  daysRemaining: number;
  isAchieved: boolean;
  updatedAt: string;
}

function getCurrentWeekRange(): { start: Date; end: Date } {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(now);
  monday.setDate(now.getDate() - daysToMonday);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { start: monday, end: sunday };
}

function getDaysRemainingInWeek(weekEnd: Date): number {
  return Math.ceil((weekEnd.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

function buildWeeklyGoal(
  userId: string,
  data: { storiesCompleted: number; targetStories: number; progressStories: number }
): WeeklyGoal {
  const { start, end } = getCurrentWeekRange();
  return {
    userId,
    targetStories: data.targetStories,
    currentProgress: data.storiesCompleted,
    progressPercentage: data.progressStories,
    weekStartDate: start.toISOString(),
    weekEndDate: end.toISOString(),
    daysRemaining: getDaysRemainingInWeek(end),
    isAchieved: data.storiesCompleted >= data.targetStories,
    updatedAt: new Date().toISOString(),
  };
}

export async function getWeeklyGoal(userId: string): Promise<WeeklyGoal> {
  const data = await usersAPI.getWeeklyGoal();
  return buildWeeklyGoal(userId, data);
}

export async function updateWeeklyGoal(userId: string, targetStories: number): Promise<WeeklyGoal> {
  if (targetStories < 1) throw new Error('Weekly goal must be at least 1 story');
  if (targetStories > 50) throw new Error('Weekly goal cannot exceed 50 stories');

  const data = await ApiClient.patch<{
    storiesCompleted: number;
    targetStories: number;
    progressStories: number;
  }>('/users/me/goals/weekly', { targetStories });

  return buildWeeklyGoal(userId, data);
}

/** Progress is updated automatically by the backend on challenge submission — use getWeeklyGoal to refresh. */
export async function incrementWeeklyProgress(userId: string): Promise<WeeklyGoal> {
  return getWeeklyGoal(userId);
}

export async function getWeeklyGoalHistory(_userId: string, weeks = 4): Promise<WeeklyGoal[]> {
  if (weeks < 1 || weeks > 52) throw new Error('weeks must be between 1 and 52');
  return ApiClient.get<WeeklyGoal[]>(`/users/me/goals/history?weeks=${weeks}`);
}
